import { useEffect, useRef, useState } from 'react'

interface UsePollSolicitudOptions<T> {
  fetcher: () => Promise<T>
  isTerminal: (data: T) => boolean
  enabled: boolean
  /** Cap absoluto de polls. Default 30 (≈4 min con los intervalos de abajo). */
  maxPolls?: number
}

interface PollState<T> {
  data: T | null
  loading: boolean
  error: Error | null
  /**
   * El polling se detuvo por haber agotado `maxPolls` y el dato NUNCA llegó a
   * ser terminal. Lo necesita quien dibuje un spinner: mientras el hook
   * consulta, la pantalla puede prometer "esto se actualiza solo"; cuando el
   * cap se agota, esa promesa deja de ser cierta y hay que decirlo.
   *
   * No se confunde con `error`: un fallo de red deja `error` cargado y el
   * polling SIGUE reintentando.
   */
  capAlcanzado: boolean
}

/**
 * Hook de polling adaptativo para solicitudes de capacitaciones IA (DD13).
 *
 * Intervalos:
 *  - 3s  durante los primeros 30s
 *  - 10s desde los 30s hasta los 5min
 *  - 60s  después de los 5min
 *
 * Límites:
 *  - Cap absoluto de `maxPolls` polls (default 30). Superado el cap, `error`
 *    recibe "Polling cap alcanzado".
 *  - Para automáticamente cuando `isTerminal(data)` retorna true.
 *  - Limpia el timeout en unmount o cuando `enabled` pasa a false.
 *
 * IMPORTANTE: `fetcher` e `isTerminal` deben ser referencias estables (useCallback)
 * en el componente consumidor. Si se pasan inline, el hook reiniciará el polling
 * en cada render porque son nuevas referencias.
 */
export function usePollSolicitud<T>({
  fetcher,
  isTerminal,
  enabled,
  maxPolls = 30,
}: UsePollSolicitudOptions<T>): PollState<T> {
  const [state, setState] = useState<PollState<T>>({
    data: null,
    loading: enabled,
    error: null,
    capAlcanzado: false,
  })

  const timeoutRef = useRef<number | undefined>(undefined)
  const pollCountRef = useRef(0)
  const startTimeRef = useRef<number>(Date.now())
  const enabledRef = useRef(enabled)

  useEffect(() => {
    enabledRef.current = enabled
  }, [enabled])

  useEffect(() => {
    if (!enabled) {
      if (timeoutRef.current !== undefined) window.clearTimeout(timeoutRef.current)
      return
    }

    pollCountRef.current = 0
    startTimeRef.current = Date.now()
    setState({ data: null, loading: true, error: null, capAlcanzado: false })

    const computeNextInterval = (): number => {
      const elapsed = Date.now() - startTimeRef.current
      if (elapsed < 30_000) return 3_000
      if (elapsed < 300_000) return 10_000
      return 60_000
    }

    const poll = async () => {
      if (!enabledRef.current) return
      if (pollCountRef.current >= maxPolls) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: new Error('Polling cap alcanzado'),
          capAlcanzado: true,
        }))
        return
      }

      pollCountRef.current += 1

      try {
        const data = await fetcher()
        setState({ data, loading: false, error: null, capAlcanzado: false })
        if (isTerminal(data)) return
        if (!enabledRef.current) return
        timeoutRef.current = window.setTimeout(poll, computeNextInterval())
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err))
        setState((prev) => ({ ...prev, loading: false, error }))
        if (!enabledRef.current) return
        timeoutRef.current = window.setTimeout(poll, computeNextInterval())
      }
    }

    poll()

    return () => {
      if (timeoutRef.current !== undefined) window.clearTimeout(timeoutRef.current)
    }
  }, [enabled, fetcher, isTerminal, maxPolls])

  return state
}
